import { useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { CallJourney, JourneyRead } from "@starsach/shared-types";
import { useCallJourney, useContactAudit } from "../api/hooks";
import { Trace } from "../components/Trace";
import { Card, DataTable, ErrorState, LoadingState, NotAvailable, PageHeader, RelativeTime, StatusPill, btnSecondary, sentence } from "../components/ui";
import { buildTrace, minutes, type Span } from "../lib/trace";
import { flagsFor } from "../lib/trace-flags";
import { useFormat } from "../lib/useFormat";

/**
 * requirements.md 8.5: one timeline per contact. The spine - legs, steps,
 * bot turns, tool calls, queue, handoff, agent - renders as soon as the
 * contact chain and flow trace are in; each verbatim panel fills in as
 * its source completes, or says why it can't. The flag strip says what
 * went wrong before anyone reads a span; "What Gairm read" says what it
 * cost the customer in reads, and how to check that in their CloudTrail.
 */
export function CallJourneyPage() {
  const { environmentId = "", contactId = "" } = useParams();
  const q = useCallJourney(environmentId, contactId);
  const [selected, setSelected] = useState<Span | null>(null);
  const [playhead, setPlayhead] = useState<number | undefined>(undefined);
  const audioRef = useRef<HTMLAudioElement>(null);
  const trace = useMemo(() => (q.data ? buildTrace(q.data) : undefined), [q.data]);
  const flags = useMemo(() => (q.data ? flagsFor(q.data) : []), [q.data]);
  if (q.isPending) return <LoadingState label="Starting call reconstruction" />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const j = q.data;

  const seek = (offsetMs: number) => {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = offsetMs / 1000;
    void a.play().catch(() => undefined);
  };

  return (
    <>
      <PageHeader
        title="Call journey"
        subtitle={`Contact ${contactId}`}
        actions={
          <div className="flex items-center gap-3 text-sm">
            <StatusPill status={j.status} tone={j.status === "complete" ? "ok" : j.status === "failed" ? "critical" : "warning"} />
            <Link to={`/journeys/${environmentId}/contacts`} className="underline">Back to journeys</Link>
          </div>
        }
      />
      {j.status === "assembling" && <p role="status" aria-live="polite" className="mb-4 text-sm text-neutral-600 dark:text-neutral-400">Assembling from the customer's account - flow logs, Lambda and Lex logs, Contact Lens and the recording are queried in parallel. Panels fill in as they complete.</p>}
      {j.status === "failed" && <div role="alert" className="mb-4 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-900 dark:bg-red-950 dark:text-red-100">Reconstruction failed: {j.error ?? "unknown error"}. Check the customer's JourneyInspectorRole and declared log groups.</div>}

      {flags.length > 0 && (
        <ul aria-label="Flags" className="mb-4 flex flex-wrap gap-2">
          {flags.map((f) => <li key={f.text}><StatusPill status={f.text} tone={f.severity === "critical" ? "critical" : f.severity === "warning" ? "warning" : "neutral"} /></li>)}
        </ul>
      )}
      {j.status === "complete" && flags.length === 0 && <p className="mb-4 text-sm text-neutral-600 dark:text-neutral-400">Nothing flagged on this contact.</p>}

      <Card title="Timeline" className="mb-6">
        {trace && <Trace data={trace} selected={selected} onSelect={setSelected} playheadMs={playhead} />}
        {selected && (
          <div className="mt-3 rounded border border-neutral-200 p-3 text-sm dark:border-neutral-800" aria-live="polite">
            <p><span className="font-medium">{selected.label}</span> <span className="text-neutral-500">· {selected.lane}</span></p>
            <p className="mt-1">{selected.detail}</p>
            {trace && <p className="mt-1 text-xs text-neutral-500">{minutes(selected.start - trace.t0)} to {minutes(selected.end - trace.t0)} into the contact{selected.error ? " · problem" : ""}</p>}
          </div>
        )}
      </Card>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="grid gap-6 lg:col-span-2">
          <Panel title="Recording and transcript (Contact Lens)" p={mergedStatus(j)}>
            {j.recording?.status === "ok" && j.recording.url && (
              <>
                {/* eslint-disable-next-line jsx-a11y/media-has-caption -- a captions track is always present below; the rule cannot see through the conditional */}
                <audio ref={audioRef} controls className="w-full" aria-label="Call recording" onTimeUpdate={(e) => setPlayhead(e.currentTarget.currentTime * 1000)}>
                  <source src={j.recording.url} />
                  {/* The Contact Lens transcript doubles as the captions track when it exists. */}
                  {j.agent?.status === "ok" && j.agent.transcript.length > 0 ? (
                    <track kind="captions" srcLang="en" label="Transcript" src={vttFrom(j.agent.transcript)} default />
                  ) : (
                    <track kind="captions" srcLang="en" label="No transcript available" src="data:text/vtt,WEBVTT" />
                  )}
                  Your browser cannot play this recording.
                </audio>
                <p className="mt-1 mb-3 text-xs text-neutral-500">Link expires <RelativeTime iso={j.recording.expiresAt} />. Playing moves the marker on the timeline; a transcript line plays from there.{j.agent?.status !== "ok" && " No transcript is available for captions."}</p>
              </>
            )}
            {j.recording?.status !== "ok" && j.recording?.note && <p className="mb-3 text-sm text-neutral-600 dark:text-neutral-400">Recording: {j.recording.note}</p>}
            {j.agent?.status === "ok" ? (
              <>
                {j.agent.categories.length > 0 && <p className="mb-2 flex flex-wrap gap-1">{j.agent.categories.map((c) => <StatusPill key={c} status={c} tone="neutral" />)}</p>}
                <ol className="space-y-1 text-sm">
                  {j.agent.transcript.map((t, i) => (
                    <li key={i} className={t.participant === "AGENT" ? "text-accent-strong" : ""}>
                      {j.recording?.status === "ok" ? (
                        <button type="button" className="font-mono text-xs text-neutral-500 underline-offset-2 hover:underline" onClick={() => seek(t.beginOffsetMs)} aria-label={`Play from ${(t.beginOffsetMs / 1000).toFixed(0)} seconds`}>{(t.beginOffsetMs / 1000).toFixed(0)}s</button>
                      ) : (
                        <span className="font-mono text-xs text-neutral-500">{(t.beginOffsetMs / 1000).toFixed(0)}s</span>
                      )}{" "}
                      <span className="font-medium">{t.participant}</span>: {t.content}
                      {t.sentiment && t.sentiment !== "NEUTRAL" && <span className="ml-1 text-xs text-neutral-500">({t.sentiment.toLowerCase()})</span>}
                    </li>
                  ))}
                </ol>
              </>
            ) : j.agent?.note ? <NotAvailable what="Transcript" why={j.agent.note} /> : null}
          </Panel>

          <Panel title="Flow steps" p={j.timeline}>
            <ol className="relative space-y-0 border-l-2 border-neutral-200 pl-4 text-sm dark:border-neutral-700">
              {j.timeline?.steps.map((s, i) => (
                <li key={i} className={`py-1.5 ${s.error ? "text-red-700 dark:text-red-300" : ""}`}>
                  <span className="font-mono text-xs text-neutral-500"><Time iso={s.timestamp} /></span>{" "}
                  <span className="font-medium">{s.identifier ?? s.moduleType}</span>
                  <span className="text-neutral-500"> · {s.moduleType}{s.flowName ? ` · ${s.flowName}` : ""}</span>
                  {s.dwellMs !== undefined && <span className="text-neutral-500"> · {(s.dwellMs / 1000).toFixed(1)}s</span>}
                  {s.error && <span className="ml-1 font-semibold">error</span>}
                </li>
              ))}
            </ol>
          </Panel>

          <Panel title="Tool calls (Lambda)" p={j.lambdas}>
            {j.lambdas?.invocations.map((inv, i) => (
              <details key={i} className="mb-2 rounded border border-neutral-200 p-2 dark:border-neutral-800">
                <summary className="cursor-pointer text-sm"><span className="font-mono">{inv.functionName}</span> · <RelativeTime iso={inv.timestamp} />{inv.durationMs !== undefined && <> · {inv.durationMs} ms</>}</summary>
                <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap font-mono text-xs">{inv.logLines.join("\n")}</pre>
              </details>
            ))}
          </Panel>

          <Panel title="Data dips (CRM / third-party lookups)" p={j.dataDips}>
            {j.dataDips?.items.map((d, i) => (
              <div key={i} className="mb-2 grid gap-2 text-xs md:grid-cols-2">
                <pre className="overflow-auto rounded bg-neutral-100 p-2 dark:bg-neutral-800"><span className="font-semibold">{d.functionName} request</span>{"\n"}{JSON.stringify(d.request, null, 2)}</pre>
                <pre className="overflow-auto rounded bg-neutral-100 p-2 dark:bg-neutral-800"><span className="font-semibold">response</span>{"\n"}{JSON.stringify(d.response, null, 2)}</pre>
              </div>
            ))}
          </Panel>
        </div>

        <div className="grid gap-6">
          <Legs legs={j.legs ?? []} />
          <Panel title="Bot" p={j.bot}>
            <ol className="space-y-2 text-sm">
              {j.bot?.turns.map((t, i) => (
                <li key={i} className="rounded border border-neutral-200 p-2 dark:border-neutral-800">
                  <p className="text-xs text-neutral-500"><RelativeTime iso={t.timestamp} /> · {t.intent ?? "no intent"}{t.fallback && <span className="ml-1 text-amber-700 dark:text-amber-300">fallback</span>}</p>
                  {t.utterance && <p className="mt-1">“{t.utterance}”</p>}
                  {t.slots && Object.keys(t.slots).length > 0 && <p className="mt-1 font-mono text-xs">{Object.entries(t.slots).map(([k, v]) => `${k}=${v}`).join(" ")}</p>}
                </li>
              ))}
            </ol>
          </Panel>
          <Reads environmentId={environmentId} contactId={contactId} journey={j} />
        </div>
      </div>
    </>
  );
}

/** The recording and the transcript share a card: one is the other's captions, and the playhead ties both to the timeline. */
function mergedStatus(j: CallJourney): { status: "ok" | "not_available" | "error"; note?: string } | undefined {
  if (!j.recording && !j.agent) return undefined;
  if (j.recording?.status === "ok" || j.agent?.status === "ok") return { status: "ok" };
  if (j.recording?.status === "error" || j.agent?.status === "error") return { status: "error", note: j.agent?.note ?? j.recording?.note };
  const note = [j.recording?.note, j.agent?.note].filter(Boolean).join(" ");
  return note ? { status: "not_available", note } : { status: "not_available" };
}

function Panel({ title, p, children }: { title: string; p: { status: "ok" | "not_available" | "error"; note?: string | undefined } | undefined; children: React.ReactNode }) {
  return (
    <Card title={title}>
      {!p || (p.status === "not_available" && !p.note) ? <LoadingState label="Waiting" /> : null}
      {p?.status === "not_available" && p.note && <NotAvailable what={title} why={p.note} />}
      {p?.status === "error" && <ErrorState error={{ message: p.note ?? "Source failed" }} />}
      {p?.status === "ok" && children}
    </Card>
  );
}

function Time({ iso }: { iso: string }) {
  const fmt = useFormat();
  return <>{fmt.time(iso)}</>;
}

function Legs({ legs }: { legs: CallJourney["legs"] }) {
  const fmt = useFormat();
  if (legs.length === 0) return <Card title="Contact"><LoadingState label="Reading the contact record" /></Card>;
  return (
    <Card title={legs.length > 1 ? `Transfer chain (${legs.length} legs)` : "Contact"}>
      <ol className="space-y-2 text-sm">
        {legs.map((l, i) => (
          <li key={l.contactId} className="rounded border border-neutral-200 p-2 dark:border-neutral-800">
            <p className="font-mono text-xs text-neutral-500">{i + 1}. {l.contactId}{l.previousContactId && <> ← {l.previousContactId}</>}</p>
            <p className="mt-1">{l.initiationMethod ?? "?"} {l.channel ?? ""} · queue {l.queueName ?? "-"} · agent {l.agentId ?? "none"}</p>
            <p className="text-neutral-600 dark:text-neutral-400">{l.customerEndpoint ?? "?"} → {l.systemEndpoint ?? "?"} · {l.initiationTimestamp ? fmt.when(l.initiationTimestamp) : "?"}{l.disconnectTimestamp && <> → {fmt.time(l.disconnectTimestamp)}</>} · {l.disconnectReason ?? ""}</p>
            {(l.audioQuality || l.agentDevice || l.potentialDisconnectIssue) && (
              <p className="mt-1 text-xs text-neutral-600 dark:text-neutral-400">
                {l.audioQuality?.agent && <>Audio, agent side: {l.audioQuality.agent.score ?? "?"}{l.audioQuality.agent.issues.length ? ` (${l.audioQuality.agent.issues.map(sentence).join(", ")})` : ""}. </>}
                {l.audioQuality?.customer && <>Customer side: {l.audioQuality.customer.score ?? "?"}{l.audioQuality.customer.issues.length ? ` (${l.audioQuality.customer.issues.map(sentence).join(", ")})` : ""}. </>}
                {l.agentDevice && <>Agent on {[l.agentDevice.platform, l.agentDevice.version, l.agentDevice.os].filter(Boolean).join(" ")}. </>}
                {l.potentialDisconnectIssue && <>Disconnect issue: {sentence(l.potentialDisconnectIssue)}.</>}
              </p>
            )}
          </li>
        ))}
      </ol>
    </Card>
  );
}

const EVENT_NOTE: Record<JourneyRead["event"], string> = {
  management: "always in CloudTrail",
  data: "only with a data-event selector",
  presign: "a GetObject when played",
};

/** ADR-0015 section 9: Gairm's half of the claim, beside the query that checks it against the customer's half. */
function Reads({ environmentId, contactId, journey }: { environmentId: string; contactId: string; journey: CallJourney }) {
  const fmt = useFormat();
  const audit = useContactAudit(environmentId, contactId);
  const [copied, setCopied] = useState(false);
  const reads = journey.reads ?? [];
  const query = lakeQuery(contactId);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(query);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };
  return (
    <Card title="What Gairm read for this contact">
      {journey.requestedBy && <p className="mb-3 text-sm">Assembled for <span className="font-medium">{journey.requestedBy}</span>{journey.requestedAt && <> <RelativeTime iso={journey.requestedAt} /></>}, through the customer's opt-in role.</p>}
      <DataTable
        caption="Reads made in the customer's account"
        rows={reads}
        rowKey={(r) => `${r.at}-${r.api}-${r.resource}`}
        filter={false}
        empty={<p className="text-sm text-neutral-600 dark:text-neutral-400">{journey.status === "assembling" ? "Reads are listed as they happen." : "No reads were recorded for this assembly."}</p>}
        columns={[
          { key: "at", header: "When", render: (r) => fmt.time(r.at) },
          { key: "api", header: "Call", render: (r) => <span className="font-mono text-xs">{r.api}</span> },
          { key: "resource", header: "On", render: (r) => <span className="break-all font-mono text-xs">{r.resource}</span> },
          { key: "event", header: "In their CloudTrail", render: (r) => <span className="text-xs">{EVENT_NOTE[r.event]}</span> },
        ]}
      />
      <p className="mt-2 text-xs text-neutral-500">S3 reads are data events: the customer's trail records them only with a selector for that prefix. The recording is read by the browser when played, under this session's identity.</p>

      <h3 className="mt-4 text-sm font-medium">People who opened or searched for this contact</h3>
      {audit.isPending && <LoadingState label="Reading the audit log" />}
      {audit.isError && <ErrorState error={audit.error} onRetry={() => void audit.refetch()} />}
      {audit.data && (
        <>
          <DataTable
            caption="Audit entries for this contact"
            rows={audit.data.entries}
            rowKey={(e) => e.id}
            filter={false}
            empty={<p className="text-sm text-neutral-600 dark:text-neutral-400">No audit entries yet - the first look is being recorded.</p>}
            columns={[
              { key: "when", header: "When", render: (e) => fmt.when(e.timestamp) },
              { key: "who", header: "Who", render: (e) => e.actorName ? `${e.actorName} (${e.actor})` : e.actor },
              { key: "what", header: "What", render: (e) => sentence(e.action.replace("journey.", "")) },
            ]}
          />
          {audit.data.truncated && <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">Only the most recent entries were checked.</p>}
        </>
      )}

      <h3 className="mt-4 text-sm font-medium">Check it in the customer's CloudTrail</h3>
      <p className="mt-1 text-xs text-neutral-600 dark:text-neutral-400">Every read above is an API call in their account. This CloudTrail Lake query lists them; the session name carries the contact id.</p>
      <pre className="mt-2 overflow-x-auto rounded bg-neutral-100 p-2 font-mono text-xs dark:bg-neutral-800" aria-label="CloudTrail Lake query">{query}</pre>
      <div className="mt-2 flex items-center gap-3">
        <button type="button" className={btnSecondary} onClick={() => void copy()}>Copy query</button>
        <span role="status" aria-live="polite" className="text-xs text-neutral-500">{copied ? "Copied" : ""}</span>
      </div>
    </Card>
  );
}

export function lakeQuery(contactId: string): string {
  return [
    "SELECT eventTime, eventName, requestParameters",
    "FROM <your event data store>",
    "WHERE (userIdentity.sessionContext.sessionIssuer.arn LIKE '%-journey-inspector'",
    "    OR userIdentity.sessionContext.sessionIssuer.arn LIKE '%-analysis')",
    `  AND userIdentity.principalId LIKE '%gairm-portal-%-${contactId}'`,
    "ORDER BY eventTime",
  ].join("\n");
}

/** WebVTT from Contact Lens turns; each cue runs until the next turn starts. */
function vttFrom(transcript: NonNullable<CallJourney["agent"]>["transcript"]): string {
  const ts = (ms: number) => new Date(ms).toISOString().slice(11, 23);
  const cues = transcript.map((t, i) => {
    const end = transcript[i + 1]?.beginOffsetMs ?? t.beginOffsetMs + 4000;
    return `${ts(t.beginOffsetMs)} --> ${ts(Math.max(end, t.beginOffsetMs + 500))}\n${t.participant}: ${t.content.replace(/\n/g, " ")}`;
  });
  return `data:text/vtt;charset=utf-8,${encodeURIComponent(`WEBVTT\n\n${cues.join("\n\n")}`)}`;
}
